// src/components/ClientSnapshot.jsx
// Expandable client snapshot card showing key info at a glance

import React, { useState, useEffect } from 'react';
import {
  User, MapPin, Phone, Mail, Calendar, Shield, CreditCard,
  ChevronDown, ChevronUp, Hash, FileText, Building, Link2
} from 'lucide-react';
import { formatDate, formatPhoneDisplay } from '../utils/formatters';

export default function ClientSnapshot({ client, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [carrierCoverage, setCarrierCoverage] = useState(null);

  // Fetch carrier coverage data from Patient Access
  useEffect(() => {
    if (!client?.id) return;
    const API_BASE = process.env.REACT_APP_API_URL || '';
    fetch(`${API_BASE}/health-data/coverage/${client.id}`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : { coverage: [] })
      .then(data => {
        // Get the most recent active coverage
        const activeCoverage = (data.coverage || []).find(c => !c.effective_end);
        setCarrierCoverage(activeCoverage || null);
      })
      .catch(() => setCarrierCoverage(null));
  }, [client?.id]);

  const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim();
  const address = [client.address, client.city, client.state, client.zip].filter(Boolean).join(', ');
  const mailingAddress = [client.mailingAddress, client.mailingCity, client.mailingState, client.mailingZip].filter(Boolean).join(', ');

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow">
      {/* Collapsed View - Always Visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 flex items-center gap-4 text-left hover:bg-slate-50/50 transition-colors"
      >
        {/* Avatar */}
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FFB800] to-amber-500 flex items-center justify-center text-xl font-bold text-white shadow-md shrink-0">
          {(client.firstName?.[0] || '') + (client.lastName?.[0] || '')}
        </div>

        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-slate-900 truncate">{fullName || 'Unknown Client'}</h2>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 mt-1">
            {client.dob && (
              <span className="flex items-center gap-1">
                <Calendar size={14} className="text-slate-400" />
                {formatDate(client.dob)}
              </span>
            )}
            {address && (
              <span className="flex items-center gap-1 truncate max-w-[200px]">
                <MapPin size={14} className="text-slate-400 shrink-0" />
                <span className="truncate">{address}</span>
              </span>
            )}
          </div>
        </div>

        {/* Plan Info Pills */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          {client.carrier && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-100 text-blue-800">
              <Building size={12} className="mr-1.5" />
              {client.carrier}
            </span>
          )}
          {client.plan && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-800 max-w-[180px] truncate">
              <Shield size={12} className="mr-1.5 shrink-0" />
              <span className="truncate">{client.plan}</span>
            </span>
          )}
        </div>

        {/* Expand Icon */}
        <div className="shrink-0 w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
          {expanded ? (
            <ChevronUp className="text-slate-500" size={20} />
          ) : (
            <ChevronDown className="text-slate-500" size={20} />
          )}
        </div>
      </button>

      {/* Mobile Plan Info (when collapsed) */}
      {!expanded && (client.carrier || client.plan) && (
        <div className="md:hidden px-5 pb-4 flex flex-wrap gap-2">
          {client.carrier && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-100 text-blue-800">
              <Building size={12} className="mr-1.5" />
              {client.carrier}
            </span>
          )}
          {client.plan && (
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-800">
              <Shield size={12} className="mr-1.5" />
              {client.plan}
            </span>
          )}
        </div>
      )}

      {/* Expanded View - Full Details */}
      {expanded && (
        <div className="border-t border-slate-100 p-6 bg-gradient-to-br from-slate-50 to-white">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Personal Details Column */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
                <User className="text-blue-500" size={16} />
                Personal Details
              </h3>
              <div className="space-y-3">
                <InfoItem label="Full Name" value={fullName} />
                <InfoItem label="Date of Birth" value={formatDate(client.dob)} />
                <InfoItem label="Phone" value={formatPhoneDisplay(client.phone)} link={client.phone ? `tel:${client.phone}` : null} />
                <InfoItem label="Email" value={client.email} link={client.email ? `mailto:${client.email}` : null} />
                <InfoItem label="Address" value={address} />
                {mailingAddress && <InfoItem label="Mailing Address" value={mailingAddress} />}
              </div>
            </div>

            {/* Medicare Details Column */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
                <CreditCard className="text-emerald-500" size={16} />
                Medicare Information
              </h3>
              <div className="space-y-3">
                <InfoItem
                  label="Medicare ID (MBI)"
                  value={client.medicareId || client.medicareBeneficiaryId || client.medicare_beneficiary_id || client.mbi}
                  highlight
                />
                <InfoItem
                  label="Social Security"
                  value={client.ssn ? `***-**-${client.ssn.slice(-4)}` : null}
                />
              </div>
            </div>

            {/* Plan Details Column */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
                <Shield className="text-purple-500" size={16} />
                Plan Details
              </h3>
              <div className="space-y-3">
                <InfoItem label="Carrier" value={client.carrier} />
                <InfoItem label="Plan Name" value={carrierCoverage?.plan_name || client.plan} />
                <InfoItem label="Plan Type" value={getPlanTypeLabel(client.planType || client.plan_type)} />
                <InfoItem label="Effective Date" value={formatDate(carrierCoverage?.effective_start || client.effectiveDate)} />
                {carrierCoverage?.subscriber_id && (
                  <InfoItem label="Subscriber ID" value={carrierCoverage.subscriber_id} />
                )}
                {carrierCoverage?.group_name && (
                  <InfoItem label="Group" value={carrierCoverage.group_name} />
                )}
                {carrierCoverage?.network && (
                  <InfoItem label="Network" value={carrierCoverage.network} />
                )}
              </div>
              {carrierCoverage?.carrier_id && (
                <div className="pt-2">
                  <span className="inline-flex items-center gap-1 text-xs text-green-600">
                    <Link2 size={12} />
                    Connected via Patient Access
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Edit Button */}
          {onEdit && (
            <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end">
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all"
              >
                <FileText size={16} />
                Edit Client Details
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value, link, highlight }) {
  const displayValue = value || '—';

  const content = (
    <div>
      <div className="text-xs text-slate-500 font-medium">{label}</div>
      <div className={`text-sm font-medium ${highlight ? 'text-emerald-600' : 'text-slate-900'}`}>
        {displayValue}
      </div>
    </div>
  );

  if (link && value) {
    return (
      <a href={link} className="block hover:bg-slate-100 rounded-lg p-2 -m-2 transition-colors">
        {content}
      </a>
    );
  }

  return <div className="p-1">{content}</div>;
}

function getPlanTypeLabel(type) {
  switch (type) {
    case 'MA': return 'Medicare Advantage';
    case 'MAPD': return 'MA with Drug Coverage';
    case 'PDP': return 'Prescription Drug Plan';
    case 'DSNP': return 'Dual Special Needs Plan';
    case 'CSNP': return 'Chronic Special Needs Plan';
    case 'ISNP': return 'Institutional SNP';
    case 'PFFS': return 'Private Fee-For-Service';
    case 'HMO': return 'HMO Plan';
    case 'PPO': return 'PPO Plan';
    default: return type || '—';
  }
}
